import type {
    AnatomySampleMap,
    DocsTranslate
} from "../types";
import {
    MarkdownViewer,
    Collapsible,
    AspectRatio,
    CodeViewer,
    Accordion,
    Resizable,
    SiteLogo,
    Loading,
    Button,
    Badge,
    Alert,
    Card,
    Text
} from "ncore-ui-kit";
import {
    shapeConfigs,
    avatarSizes,
    spaces
} from "../sampleTokens";
import {
    BellIcon,
    UserIcon
} from "../icons";

const RESIZABLE_PANEL_STYLE = {
    minHeight: avatarSizes.xLarge,
    padding: spaces.spacingMd
};

const renderCollapsible = (t: DocsTranslate, isOpen: boolean) => {
    return <Collapsible
        onOpenChange={() => undefined}
        isOpen={isOpen}
    >
        <Collapsible.Trigger/>
        <Collapsible.Content>
            <Text>
                {t("sample.body")}
            </Text>
        </Collapsible.Content>
    </Collapsible>;
};

const renderAccordion = (t: DocsTranslate, value: Array<string>) => {
    return <Accordion
        onValueChange={() => undefined}
        variant="contained"
        value={value}
    >
        <Accordion.Item
            value="profile"
        >
            <Accordion.Trigger
                subTitle={t("sample.subtitle")}
                title={t("sample.item1")}
                icon={UserIcon}
            />
            <Accordion.Content
                isInset={true}
            >
                {t("sample.body")}
            </Accordion.Content>
        </Accordion.Item>
        <Accordion.Item
            value="settings"
        >
            <Accordion.Trigger
                title={t("sample.item2")}
            />
            <Accordion.Content>
                {t("sample.body")}
            </Accordion.Content>
        </Accordion.Item>
    </Accordion>;
};

export const basicSurfaceSamples: AnatomySampleMap = {
    "Badge.Indicator": (t) => ({
        node: <Badge.Indicator
            type="danger"
            count={3}
        >
            <Button
                accessibilityLabel={t("sample.notification")}
                onPress={() => undefined}
                variant="tonal"
                icon={BellIcon}
                type="neutral"
            />
        </Badge.Indicator>
    }),
    Card: (t) => ({
        node: <Card>
            <Card.Header>
                <Card.Title>
                    {t("sample.title")}
                </Card.Title>
                <Card.Description>
                    {t("sample.subtitle")}
                </Card.Description>
                <Card.Action>
                    <Button
                        title={t("sample.cancel")}
                        onPress={() => undefined}
                        variant="outline"
                        type="neutral"
                        size="small"
                    />
                </Card.Action>
            </Card.Header>
            <Card.Content>
                <Text>
                    {t("sample.body")}
                </Text>
            </Card.Content>
            <Card.Footer>
                <Button
                    title={t("sample.action")}
                    onPress={() => undefined}
                />
            </Card.Footer>
        </Card>,
        width: shapeConfigs.drawerWidth,
        maxScale: 2
    }),
    Collapsible: (t) => ({
        states: [
            {
                node: renderCollapsible(t, false),
                label: t("sample.stateClosed")
            },
            {
                node: renderCollapsible(t, true),
                label: t("sample.stateOpen")
            }
        ],
        maxScale: 3
    }),
    Accordion: (t) => ({
        states: [
            {
                label: t("sample.stateClosed"),
                node: renderAccordion(t, [])
            },
            {
                label: t("sample.stateOpen"),
                node: renderAccordion(t, [
                    "settings"
                ])
            }
        ],
        width: shapeConfigs.drawerWidth,
        maxScale: 2
    }),
    Alert: (t) => ({
        node: <Alert
            actions={[
                {
                    title: t("sample.action"),
                    onPress: () => undefined
                }
            ]}
            description={t("sample.body")}
            onClose={() => undefined}
            title={t("sample.title")}
            isClosable={true}
            type="info"
        />,
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 2
    }),
    Resizable: (t) => ({
        node: <Resizable.PanelGroup
            compactBreakpoint={0}
        >
            <Resizable.Panel
                style={RESIZABLE_PANEL_STYLE}
            >
                <Text>
                    {t("sample.title")}
                </Text>
            </Resizable.Panel>
            <Resizable.Handle
                isGripVisible={true}
            />
            <Resizable.Panel
                style={RESIZABLE_PANEL_STYLE}
            >
                <Text>
                    {t("sample.body")}
                </Text>
            </Resizable.Panel>
        </Resizable.PanelGroup>,
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 2
    }),
    SiteLogo: (t) => ({
        node: <SiteLogo
            subTitle={t("sample.subtitle")}
            title={t("sample.brand")}
        />
    }),
    CodeViewer: (t) => ({
        node: <CodeViewer
            code={[
                "function greet() {",
                `    ${t("sample.code")}`,
                "}"
            ].join("\n")}
            language="typescript"
        />,
        width: shapeConfigs.drawerWidth,
        maxScale: 2
    }),
    MarkdownViewer: (t) => ({
        node: <MarkdownViewer
            content={[
                `## ${t("sample.title")}`,
                "",
                t("sample.markdown"),
                "",
                `- ${t("sample.item1")}`,
                `- ${t("sample.item2")}`
            ].join("\n")}
        />,
        width: shapeConfigs.drawerWidth,
        maxScale: 2
    }),
    AspectRatio: (t) => ({
        width: shapeConfigs.drawerWidth,
        node: <AspectRatio
            alt={t("sample.label")}
            ratio="16:9"
        />,
        maxScale: 2
    }),
    Loading: () => ({
        node: <Loading
            size="large"
        />
    })
};
